import { useEffect, useState } from "react";

import isNil from "lodash/isNil";
import type { LayoutChangeEvent } from "react-native";

import useScheduleEvents from "@/hooks/useScheduleEvents";
import { useAppUserSelector } from "@/store/reducers/appUser";
import { useUserPreferencesDispatch } from "@/store/reducers/userPreferences";
import JoinEventBanner from "@/ui/components/home/JoinEventBanner";
import { Box, VStack } from "@/ui/layout";
import { getTimeRange, isEventWithin15Minutes } from "@/utils/date.util";
import { getScheduleEventProps } from "@/utils/schedule.util";

const LiveEventTabHeader = () => {
  const { externalId: appUserId, isPatient: isAppUserPatient } =
    useAppUserSelector();
  const [bannerHeight, setBannerHeight] = useState<number | undefined>(
    undefined,
  );
  const [bannerWidth, setBannerWidth] = useState<number | undefined>(undefined);
  const onLayout = (event: LayoutChangeEvent) => {
    const { layout } = event.nativeEvent;
    const { height, width } = layout;
    setBannerHeight(height);
    setBannerWidth(width);
  };

  const { setIsEventBannerVisible } = useUserPreferencesDispatch();

  const { scheduleEvents } = useScheduleEvents({
    isPastEvents: false,
    isPatient: isAppUserPatient,
    pageSize: 30,
    // LiveEventTabHeader is only used for the active appUser, and not linked patients
    patientExternalId: appUserId,
  });
  const ungroupedEvents = scheduleEvents.flatMap((group) => group.data);
  const eventsHappeningSoon = ungroupedEvents.filter((event) => {
    const { isGroupClassEvent } = event;
    const { endDate, startDate } = getScheduleEventProps({
      event,
      isGroupClassEvent,
    });
    return isEventWithin15Minutes({ endDate, startDate });
  });

  // give priority to appointments happening now over group class events, but fall back if no appointments
  const soonestEvent =
    eventsHappeningSoon.find((event) => !event.isGroupClassEvent) ||
    eventsHappeningSoon[0];

  useEffect(() => {
    const isEventBannerVisible = !isNil(soonestEvent);
    setIsEventBannerVisible(isEventBannerVisible);
    return () => {
      // Ensure to reset the banner visibility when the component unmounts - for example on logout
      setIsEventBannerVisible(false);
    };
  }, [setIsEventBannerVisible, soonestEvent]);

  if (isNil(soonestEvent)) return null;

  const isGroupClass = soonestEvent.isGroupClassEvent;
  const {
    endDate,
    externalId,
    isPhoneCallEvent,
    selectedUserId: patientId,
    startDate,
    title,
  } = getScheduleEventProps({
    event: soonestEvent,
    isGroupClassEvent: isGroupClass,
  });
  const timeRange = getTimeRange({ endDate, startDate });

  const renderLiveEventBanner = () => {
    return (
      <Box onLayout={onLayout} position="absolute" width="100%">
        <JoinEventBanner
          endDate={endDate}
          eventId={externalId}
          eventTitle={title}
          isGroupClass={isGroupClass}
          isPhoneCallEvent={isPhoneCallEvent}
          patientId={patientId}
          startDate={startDate}
          timeRange={timeRange}
        />
      </Box>
    );
  };

  return (
    <VStack>
      {/* placeholder box to push all the content down, so by default the live event banner is not hiding anything underneath */}
      <Box height={bannerHeight} opacity={0} width={bannerWidth} />
      {/* actual rendered banner component, floating, so it stays at the top of screen even if scrolling the screen */}
      {renderLiveEventBanner()}
    </VStack>
  );
};

export default LiveEventTabHeader;
